import type { WeeklyChartWeek } from './types';

/**
 * Buckets timestamped counts into 12 trailing weekly buckets (oldest first),
 * labeled `W<isoWeek>`. No server-side bucketing endpoint exists anywhere in
 * the app (checked ingestion, jobs, and billing routes) — this always runs
 * client-side against raw history.
 */
export function bucketIntoWeeks(
  events: Array<{ at: string | Date | null | undefined; count: number; failed?: boolean }>,
  weekCount = 12,
  now: Date = new Date()
): WeeklyChartWeek[] {
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const endOfThisWeek = new Date(now);
  const buckets: WeeklyChartWeek[] = [];

  for (let i = weekCount - 1; i >= 0; i--) {
    const bucketEnd = new Date(endOfThisWeek.getTime() - i * weekMs);
    const bucketStart = new Date(bucketEnd.getTime() - weekMs);
    let value = 0;
    let hasFailure = false;
    let runs = 0;
    let failedRuns = 0;
    for (const event of events) {
      if (!event.at) continue;
      const at = event.at instanceof Date ? event.at : new Date(event.at);
      if (Number.isNaN(at.getTime())) continue;
      if (at >= bucketStart && at < bucketEnd) {
        value += event.count;
        runs += 1;
        if (event.failed) {
          hasFailure = true;
          failedRuns += 1;
        }
      }
    }
    buckets.push({ label: isoWeekLabel(bucketEnd), value, isFailure: hasFailure, runs, failedRuns });
  }
  return buckets;
}

function isoWeekLabel(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `W${weekNo}`;
}
